<template>
  <b-overlay :show="reportLoading" variant="white" opacity="1" no-fade>
    <template #overlay>
      <template v-if="reportLoadingMsg">
        <div class="text-center">
          <b-icon
            :icon="reportLoadingIcon"
            animation="throb"
            variant="primary"
            scale="2"
            class="mx-auto"
          ></b-icon>
          <p class="h3 mt-3 mb-0 font-weight-light">
            {{ reportLoadingMsg }}
          </p>
        </div>
      </template>
      <b-icon
        v-else
        icon="three-dots"
        animation="cylon"
        variant="primary"
        scale="2"
        shift-v="8"
        shift-h="8"
        class="position-absolute"
      ></b-icon>
    </template>

    <template v-if="reportValid">
      <div class="printable-report">
        <h4 class="printable-report__title text-primary text-center">
          LimeIQ LLC
        </h4>

        <div
          :style="{ 'min-height': reportHeight }"
          id="report-container"
          class="printable-report__report position-relative"
        ></div>
      </div>

      <ModalShareReport v-if="id" :report-id="id" />
    </template>
    <div
      v-else
      :style="{ 'min-height': reportHeight }"
      class="px-3 d-flex align-items-center justify-content-center"
    >
      <b-row>
        <b-col>
          <span class="h3 mb-3 d-block">Invalid report.</span>
          <ContactAdminButton />
        </b-col>
      </b-row>
    </div>
  </b-overlay>
</template>

<script>
import ModalShareReport from '@components/ModalShareReport'
import ContactAdminButton from '@components/ContactAdminButton'

import { powerBIReportMixin } from '@mixins'

export default {
  name: 'powerbi-report',
  mixins: [powerBIReportMixin],
  components: {
    ModalShareReport,
    ContactAdminButton
  },
  props: {
    id: {
      default: null,
      required: true
    },
    companyId: Number
  },
  inject: ['reportSvc', 'profileSvc'],
  data() {
    return {
      reportHeight: `100vh`,
      reportLoading: true,
      reportLoadingIcon: 'circle-fill',
      reportLoadingMsg: undefined
    }
  },
  computed: {
    reportParams() {
      return {
        id: this.id,
        companyId: this.companyId
      }
    }
  },
  watch: {
    reportLoading: {
      immediate: true,
      handler: function (newVal) {
        this.$emitter.emit('report-loading', newVal)
      }
    },
    reportParams: {
      deep: true,
      handler: function () {
        this.fetchReport()
      }
    }
  },
  methods: {
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this))
    }
  },
  mounted() {
    this.$set(this, 'reportLoading', true)

    this.$bus.trackEvent('error-catched', () => {
      this.reportLoading = false
    })

    this.$emitter.on('favorite-report', async (payload) => {
      await this.reportSvc.favorite(
        {
          user_id: this.profileSvc.user.id,
          report_id: payload ? null : this.id
        },
        payload ? 'set' : 'remove'
      )

      this.$emitter.emit('favorite-report-success', {
        is_favorite: payload,
        report_id: this.id
      })
    })

    this.$nextTick(() => {
      const bodyHeight = document.body.clientHeight
      const navbarHeight = this.$route.meta.hideNav
        ? 0
        : document.getElementById('main-navbar').clientHeight
      const footerHeight = this.$route.meta.hideFooter
        ? 0
        : document.getElementById('main-footer').clientHeight

      this.reportHeight = `${bodyHeight - (navbarHeight + footerHeight)}px`
    })
  },
  beforeDestroy() {
    this.reset()
  }
}
</script>

<style lang="scss">
#report-actions {
  z-index: 2;
}

.printable-report {
  &__logo,
  &__title {
    display: none;
  }

  &__report {
    iframe {
      border: 0;
      position: absolute;
      top: 0;
      left: 0;
    }
  }
}
</style>

<style>
@media print {
  @page {
    size: letter landscape;
    margin: 0;
    margin-left: 0.6in;
  }

  * {
    color: black !important;
    background: transparent !important;
    text-shadow: none !important;
    box-shadow: none !important;
    border: none !important;
    float: none !important;
    clear: none !important;
  }
  html,
  body,
  article,
  header,
  section,
  footer,
  aside,
  div {
    width: 100% !important;
  }
  body {
    height: 100% !important;
    background-color: #ffffff;
    border: 0;
    margin: 0;
  }
  a,
  a:link,
  a:visited,
  a:hover,
  a:active,
  abbr,
  acronym {
    text-decoration: none;
    border-bottom: 0 none;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  li {
    page-break-inside: avoid;
    orphans: 3;
    widows: 3;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    page-break-after: avoid;
  }
  thead {
    display: table-header-group;
  }
  tr,
  img {
    page-break-inside: avoid;
  }
  nav {
    display: none;
  }

  .router-view {
    min-height: auto !important;
  }

  .printable-report {
    margin-right: auto;
    margin-left: auto;
    display: block;
  }
  .printable-report__title {
    margin-top: 0.6in;
    margin-bottom: 0.15in;
    display: block;
  }
  .printable-report__report {
    width: 100% !important;
    min-height: auto !important;
    padding: 0;
    margin: 0;
  }
  .printable-report__report iframe {
    width: 10in !important;
    height: 6.5in !important;
    position: relative;
  }

  #main-navbar,
  #main-footer,
  #help-widget--handle {
    display: none;
  }
}
</style>
